/**
 * The "you may also like" block of the product detail and the look detail.
 *
 * Step 2 of the contract closed in SF-18404: the products no longer travel in
 * the product payload, `bylRecommendations.productsUrl` carries the URL that
 * returns them (`Recommendations-GetJSON`). The old `bylEinsteinRecommendations`
 * and `bylExplicitRecommendations` keys stay in the payload, frozen at 12, only
 * until the apps migrate — nothing here reads them any more.
 *
 * Which source won and which recommender produced it is decided by the
 * endpoint, not by the render of the page, so the analytics of SF-18188 report
 * what the response says and not what the payload guessed.
 *
 * Requirements of the host component: a `product` prop carrying
 * `bylRecommendations`.
 */
import getAnalyticsEventBus from '../../common/getAnalyticsEventBus';

const analyticsEventBus = getAnalyticsEventBus();

export default {
  data() {
    return {
      recommendedProducts: [],
      recommendationsSource: null,
      recommendationsRecommenderName: '',
    };
  },

  computed: {
    /**
     * Resolved on the server and carried by the product, which is the very same
     * object the app payload gets, so the two cannot disagree. Null means "no
     * title", and an empty heading is what hides it: BaseProductSlider only
     * renders one when there is something to render.
     * @returns {string} the title of the recommendations block
     */
    recommendationsHeading() {
      return this.product?.bylRecommendations?.title ?? '';
    },
    /**
     * Empty on the surfaces with nothing configurable, where every flag then
     * falls back to the default of the slider, which is to show everything.
     * @returns {Object} the display flags of the recommendations block
     */
    recommendationsDisplay() {
      return this.product?.bylRecommendations?.display ?? {};
    },
    /**
     * Null when the block has nothing to ask for — no recommender, or the
     * Einstein slider switched off — and then nothing is fetched or rendered.
     * @returns {?string} the URL of the endpoint that returns the products
     */
    recommendationsProductsUrl() {
      return this.product?.bylRecommendations?.productsUrl ?? null;
    },
    /**
     * @returns {boolean} whether the products shown came from Einstein, which
     *  is what the view and click tracking of the tiles reports
     */
    isRecommendationFromEinstein() {
      return this.recommendationsSource === 'einstein';
    },
  },

  watch: {
    // Immediate so the first render fetches too. The product changes under the
    // block when a colour keeps the component mounted, and with it the anchor
    // of the URL, so the block follows it.
    recommendationsProductsUrl: {
      immediate: true,
      handler: 'fetchRecommendations',
    },
  },

  methods: {
    /**
     * Asks the endpoint for the products of the block. How many arrive is a
     * server decision (the `maxproducts` of the URL, configured in the
     * `pdpRecommendationsMaxProducts` site preference), so nothing is capped
     * here.
     * @returns {void}
     */
    fetchRecommendations() {
      const url = this.recommendationsProductsUrl;

      this.recommendedProducts = [];
      this.recommendationsSource = null;
      this.recommendationsRecommenderName = '';

      if (!url) {
        return;
      }

      fetch(url)
        .then((response) => response.json())
        .then(({ recommendations, source, recommenderName }) => {
          // A reply for the product the page no longer shows is dropped.
          if (url !== this.recommendationsProductsUrl) {
            return;
          }
          this.recommendedProducts = recommendations ?? [];
          this.recommendationsSource = source ?? null;
          this.recommendationsRecommenderName = recommenderName ?? '';

          if (this.recommendedProducts.length > 0) {
            analyticsEventBus.publish(
              analyticsEventBus.EVENTS.productRecommendationsLoaded,
              {
                products: this.recommendedProducts,
                isFromEinstein: this.isRecommendationFromEinstein,
                recommenderName: this.recommendationsRecommenderName,
              }
            );
          }
        })
        .catch(() => {
          // No block at all, which is what the page showed whenever there was
          // nothing to recommend before the products moved to the endpoint.
        });
    },
  },
};
